import { useMediaQuery } from "@mui/material";
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { AddIcon, TradesIcon } from "../../assets/img/icons";
import Button from "../../components/Button";
import ConfirmModal from "../../components/ConfirmModal";
import ConnectedLayout from "../../components/ConnectedLayout";
import Header from "../../components/Header";
import DropdownMenu from "../../components/OldDropdown/DropdownMenu";
import { AuthContext } from "../../contexts/AuthContext";
import { Session } from "../../types/Session";
import { AdminUserByIdResponse } from "../../types/api/admin/user";
import CalendarSessions from "./CalendarSessions";
import NoTradeModal from "./NoTradeModal";
import TradesTopSections from "./TradesTopSections";

import { getAllRoles } from "../../services/api/admin/role";
import { fetchUserForMemberEdit, getUserById } from "../../services/api/admin/user";
import { MemberEditUser } from "../../types/api/admin/user";
import MemberModal from "../Members/MemberModal";
import { NoTradesSession } from "../../types/NoTradesSession";
import { getUserName } from "../../utils/string";
import AddSessionChoiceModal from "./Modals/AddSessionChoiceModal";
import DayDetailsModal from "./Modals/DayDetailsModal";
// ajouts de Tony
import { Timeslots } from "../../types/enums/Timeslots";
import { isAdminRole } from "../../utils/graduation";
import { TradingStyleValue } from "../../types/enums/TradingStyle";
import {
    getFirstDayOfSpecificMonth,
    getFormattedDateInput,
    getLastDayOfSpecificMonth,
    getMonthYearLabel,
    isWithinSessionEditWindow,
} from "../../utils/date";
// fin ajouts de Tony
import TradingSessionFormModal, {
    TradingSessionFormModalMode,
} from "../../components/ModalForm/TradingSessionFormModal";
import { deleteNoTradesSession } from "../../services/api/noTradesSession";

import DatePicker from "../../components/DatePicker";
import Modal from "../../components/Modal";
import ProfileIncompleteBanner from "../../components/ProfileIncompleteBanner";
import MemberDashboardHeaderActions from "../../components/MemberDashboardHeaderActions";
import Select from "../../components/Select";
import Text from "../../components/Text";
import { usePostLoginModal } from "../../contexts/PostLoginModalContext";
import { usePerformanceVisibility } from "../../hooks/usePerformanceVisibility";
import { useProfileCompletion } from "../../hooks/useProfileCompletion";
import "./style.css";
import styles from "./styles.module.scss";

const Trades: FC = () => {
    const { user, hasPermission, isStreamOn } = useContext(AuthContext)!;
    const navigate = useNavigate();
    const { currentModal, requestModal, dismissModal } = usePostLoginModal();
    const {
        isProfileComplete,
        isLoading: isProfileCompletionLoading,
        missingUserFieldLabels,
        questionsAnswered,
        questionsTotal,
        completionPercent,
    } = useProfileCompletion();

    const isMobile = useMediaQuery("(max-width: 1050px)");

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const userId =
        queryParams.has("userId") &&
        hasPermission("admin-trading-sessions", "read")
            ? queryParams.get("userId")
            : user!.id;

    const isOwnDashboard = !queryParams.has("userId");
    const canEditMemberProfile =
        !isOwnDashboard && hasPermission("admin-members", "update");

    const { hidePerformance, togglePerformance } =
        usePerformanceVisibility(user?.id, isOwnDashboard);

    const [userDashboard, setUserDashboard] = useState<
        AdminUserByIdResponse | undefined
    >();
    const [memberModalOpen, setMemberModalOpen] = useState(false);
    const [userToEdit, setUserToEdit] = useState<MemberEditUser | null>(null);
    const [memberRoles, setMemberRoles] = useState<
        { id: string; name: string }[]
    >([]);

    const dateNow = useMemo(() => new Date(), []);

    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);

    const selectedMonthQuery = queryParams.get("selectedMonth");
    const [calendarMonth, setCalendarMonth] = useState<Date>(
        selectedMonthQuery ? new Date(selectedMonthQuery) : dateNow,
    );

    const [timeslotFilter, setTimeslotFilter] = useState<string | undefined>(
        undefined,
    );
    const [challengeFilter, setChallengeFilter] = useState<string | undefined>(
        undefined,
    );
    const [tradingStyleFilter, setTradingStyleFilter] = useState<
        TradingStyleValue | undefined
    >(undefined);

    const [hasFiltersBeenModified, setHasFiltersBeenModified] =
        useState<boolean>(false);

    const [topSectionRefreshKey, setTopSectionRefreshKey] = useState(0);

    // Quand aucun filtre n'est sélectionné ou DatePicker vide, on utilise le mois affiché au calendrier
    const effectiveStartDate = useMemo(
        () =>
            hasFiltersBeenModified && startDate
                ? startDate
                : getFirstDayOfSpecificMonth(
                      calendarMonth.getFullYear(),
                      calendarMonth.getMonth(),
                  ),
        [hasFiltersBeenModified, startDate, calendarMonth],
    );
    const effectiveEndDate = useMemo(
        () =>
            hasFiltersBeenModified && endDate
                ? endDate
                : getLastDayOfSpecificMonth(
                      calendarMonth.getFullYear(),
                      calendarMonth.getMonth(),
                  ),
        [hasFiltersBeenModified, endDate, calendarMonth],
    );

    // Mapping des créneaux par catégorie (utiliser les clés d'enum, pas les labels)
    const timeslotCategories = useMemo(
        () => ({
            morning: ["H8_10", "H9_11", "H10_12"],
            afternoon: ["H15_17"],
            evening: ["H20_22"],
        }),
        [],
    );

    // ------------------ Start - Modals state  ------------------

    const [tradingSessionModalState, setTradingSessionModalState] = useState<{
        open: boolean;
        mode: TradingSessionFormModalMode;
        session: Session | undefined;
        initialDateForAdd?: string;
    }>({ open: false, mode: "add", session: undefined });

    const [modalDayDetailsState, setModalDayDetailsState] = useState<{
        open: boolean;
        date: string | undefined;
        sessions: (Session | NoTradesSession)[] | undefined;
    }>({ open: false, date: undefined, sessions: undefined });

    const [modalNoTradeState, setModalNoTradeState] = useState<{
        open: boolean;
        mode: TradingSessionFormModalMode;
        session: NoTradesSession | undefined;
        initialDateForAdd?: string;
    }>({ open: false, mode: "add", session: undefined });

    const [addSessionChoiceState, setAddSessionChoiceState] = useState<{
        open: boolean;
        date: Date | null;
    }>({ open: false, date: null });

    const [confirmModal, setConfirmModal] = useState<{
        open: boolean;
        session: Session | NoTradesSession | undefined;
        isLoading?: boolean;
    }>({ open: false, session: undefined });

    // ------------------ End - Modals state  ------------------

    // ------------------ Start - Handle modal methods ------------------
    const handleTradingSessionModalOpen = (
        mode: TradingSessionFormModalMode,
        session?: Session | undefined,
        options?: { initialDateForAdd?: string },
    ) => {
        setTradingSessionModalState({
            open: true,
            mode,
            session,
            initialDateForAdd:
                mode === "add" ? options?.initialDateForAdd : undefined,
        });
    };

    const handleTradingSessionModalClose = () =>
        setTradingSessionModalState({
            open: false,
            mode: "add",
            session: undefined,
            initialDateForAdd: undefined,
        });

    const handleDayDetailsOpen = (
        date: string | undefined,
        sessions: (Session | NoTradesSession)[],
    ) => setModalDayDetailsState({ open: true, date, sessions });
    const handleDayDetailsClose = () =>
        setModalDayDetailsState({
            open: false,
            date: undefined,
            sessions: undefined,
        });

    // NoTrade modal functions
    const handleNoTradeOpen = (
        mode: TradingSessionFormModalMode,
        session?: NoTradesSession | undefined,
        options?: { initialDateForAdd?: string },
    ) => {
        setModalNoTradeState({
            open: true,
            mode,
            session,
            initialDateForAdd:
                mode === "add" ? options?.initialDateForAdd : undefined,
        });
    };

    const handleNoTradeClose = () => {
        setModalNoTradeState({
            open: false,
            mode: "add",
            session: undefined,
            initialDateForAdd: undefined,
        });
        setModalDayDetailsState((prev) => ({
            ...prev,
            sessions: undefined,
        }));
    };

    const isCalendarDayInFuture = (d: Date) => {
        const cell = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const t = new Date();
        const today = new Date(t.getFullYear(), t.getMonth(), t.getDate());
        return cell.getTime() > today.getTime();
    };

    /**
     * Jours où le clic « ajouter depuis le calendrier » est actif : pas de toast / modale si refus —
     * la case n’est tout simplement pas cliquable (voir CustomDayCell).
     */
    const isCalendarEmptyDayAddAllowed = (date: Date) => {
        if (isCalendarDayInFuture(date)) return false;
        if (!isProfileComplete && !isProfileCompletionLoading) return false;
        const isAdmin = isAdminRole(user?.role);
        if (!isAdmin && !isWithinSessionEditWindow(date)) return false;
        return true;
    };

    const handleEmptyCalendarDayClick = (date: Date) => {
        setAddSessionChoiceState({ open: true, date });
    };

    const handleCloseAddSessionChoice = () =>
        setAddSessionChoiceState({ open: false, date: null });

    const handleChooseTradesFromCalendar = () => {
        const d = addSessionChoiceState.date;
        handleCloseAddSessionChoice();
        if (!d) return;
        handleTradingSessionModalOpen("add", undefined, {
            initialDateForAdd: getFormattedDateInput(d),
        });
    };

    const handleChooseNoTradesFromCalendar = () => {
        const d = addSessionChoiceState.date;
        handleCloseAddSessionChoice();
        if (!d) return;
        handleNoTradeOpen("add", undefined, {
            initialDateForAdd: getFormattedDateInput(d),
        });
    };

    // Confirm modal functions
    const handleConfirmOpen = (session: Session | NoTradesSession) => {
        setConfirmModal({ open: true, session });
    };

    const handleConfirmClose = () => {
        setConfirmModal({
            open: false,
            session: undefined,
            isLoading: false,
        });
    };

    const handleConfirm = async () => {
        try {
            setConfirmModal({ ...confirmModal, isLoading: true });

            if (!("Trade" in confirmModal.session!)) {
                await deleteNoTradesSession(confirmModal.session!.id);
                toast.success(`Session supprimée avec succès`);
                setTopSectionRefreshKey((k) => k + 1);
                handleDayDetailsClose();
            }
        } catch (error) {
            toast.error(
                "Une erreur s'est produite, veuillez réessayer plus tard",
            );
        } finally {
            handleConfirmClose();
        }
    };

    // ------------------ End - Handle modal methods ------------------

    const handleChangeStartDate = (date: Date) => {
        setStartDate(date);
        setHasFiltersBeenModified(true);
    };

    const handleChangeEndDate = (date: Date) => {
        setEndDate(date);
        setCalendarMonth(new Date(date.getFullYear(), date.getMonth(), 1));
        setHasFiltersBeenModified(true);
    };

    const handleTimeslotFilterChange = (value: string) => {
        setTimeslotFilter(value || undefined);
        setHasFiltersBeenModified(true);
    };

    const handleChallengeFilterChange = (value: string) => {
        setChallengeFilter(value || undefined);
        setHasFiltersBeenModified(true);
    };

    const handleTradingStyleFilterChange = (value: string) => {
        setTradingStyleFilter(
            (value as TradingStyleValue) || undefined,
        );
        setHasFiltersBeenModified(true);
    };

    const handleResetFilters = () => {
        const now = new Date();
        setStartDate(undefined);
        setEndDate(undefined);
        setTimeslotFilter(undefined);
        setChallengeFilter(undefined);
        setTradingStyleFilter(undefined);
        setCalendarMonth(new Date(now.getFullYear(), now.getMonth(), 1));
        setHasFiltersBeenModified(false);
    };

    const handleAddSessionClick = () => {
        if (!isProfileComplete && !isProfileCompletionLoading) {
            requestModal("profileIncomplete");
            return;
        }
    };

    useEffect(() => {
        if (!canEditMemberProfile) return;
        getAllRoles().then(setMemberRoles);
    }, [canEditMemberProfile]);

    const handleEditProfile = async () => {
        try {
            const editUser = await fetchUserForMemberEdit(userId!, memberRoles);
            if (!editUser) {
                toast.error(
                    "Profil incomplet (email, rôle ou statut manquant). Vérifiez la réponse GET /v3/admin/users/:id.",
                );
                return;
            }
            setUserToEdit(editUser);
            setMemberModalOpen(true);
        } catch {
            toast.error("Impossible de charger le profil du membre.");
        }
    };

    const headerProps = {
        optionalButton: (
            <>
                {isOwnDashboard && (
                    isProfileComplete || isProfileCompletionLoading ? (
                        <DropdownMenu
                            trigger={
                                <Button
                                    labelOptions={{ noWrap: true }}
                                    Icon={AddIcon}
                                    size="medium"
                                    label={
                                        !isMobile
                                            ? "Ajouter une nouvelle session"
                                            : ""
                                    }
                                />
                            }
                            options={[
                                {
                                    label: "Trade",
                                    onClick: () =>
                                        handleTradingSessionModalOpen("add"),
                                },
                                {
                                    label: "No trade",
                                    onClick: () => handleNoTradeOpen("add"),
                                },
                            ]}
                        />
                    ) : (
                        <Button
                            labelOptions={{ noWrap: true }}
                            Icon={AddIcon}
                            size="medium"
                            label={
                                !isMobile
                                    ? "Ajouter une nouvelle session"
                                    : ""
                            }
                            onClick={handleAddSessionClick}
                        />
                    )
                )}
            </>
        ),
        titleOptionalButtons: !isOwnDashboard && userId && (
            <MemberDashboardHeaderActions
                userId={userId}
                currentPage="trades"
                canEditMemberProfile={canEditMemberProfile}
                onEditProfile={handleEditProfile}
            />
        ),
    };

    let title = "Chargement...";
    if (isOwnDashboard) {
        title = "Mes trades";
    } else if (userDashboard) {
        const { firstname, lastname, pseudo, role } = userDashboard;
        const userName = getUserName(
            firstname ?? "",
            lastname ?? "",
            pseudo ?? "",
            isStreamOn,
        );
        title = `${isMobile ? "" : "Trades de "}${userName}${role ? ` - ${role}` : ""}`;
    }

    const refreshUserDashboard = useCallback(() => {
        if (!isOwnDashboard) {
            getUserById(userId!).then((user) => {
                setUserDashboard(user);
            });
        }
    }, [isOwnDashboard, userId]);

    useEffect(() => {
        refreshUserDashboard();
    }, [refreshUserDashboard]);

    // Ouvrir la popup profil incomplet après connexion si les questions ne sont pas remplies
    useEffect(() => {
        if (
            isOwnDashboard &&
            !isProfileCompletionLoading &&
            !isProfileComplete &&
            sessionStorage.getItem("showProfileIncompleteModal") === "1"
        ) {
            sessionStorage.removeItem("showProfileIncompleteModal");
            requestModal("profileIncomplete");
        }
    }, [
        isOwnDashboard,
        isProfileCompletionLoading,
        isProfileComplete,
        requestModal,
    ]);

    return (
        <ConnectedLayout pageContentClassName={styles.connectedLayout}>
            <MemberModal
                open={memberModalOpen}
                handleClose={() => {
                    setMemberModalOpen(false);
                    setUserToEdit(null);
                }}
                user={userToEdit ?? undefined}
                roles={memberRoles}
                onUserUpdated={refreshUserDashboard}
            />
            <TradingSessionFormModal
                mode={tradingSessionModalState.mode}
                open={tradingSessionModalState.open}
                onClose={handleTradingSessionModalClose}
                onSuccess={() => setTopSectionRefreshKey((k) => k + 1)}
                session={tradingSessionModalState.session}
                initialDateForAdd={tradingSessionModalState.initialDateForAdd}
                setSession={(session: Session) =>
                    setTradingSessionModalState((prev) => ({
                        ...prev,
                        session,
                        initialDateForAdd: undefined,
                    }))
                }
            />
            <AddSessionChoiceModal
                open={addSessionChoiceState.open}
                onClose={handleCloseAddSessionChoice}
                date={addSessionChoiceState.date}
                onChooseTrades={handleChooseTradesFromCalendar}
                onChooseNoTrades={handleChooseNoTradesFromCalendar}
            />
            <DayDetailsModal
                open={modalDayDetailsState.open}
                onClose={handleDayDetailsClose}
                handleNoTradeOpen={handleNoTradeOpen}
                handleConfirmOpen={handleConfirmOpen}
                sessions={
                    modalDayDetailsState.sessions
                        ? modalDayDetailsState.sessions
                        : []
                }
            />
            <NoTradeModal
                mode={modalNoTradeState.mode}
                open={modalNoTradeState.open}
                handleClose={handleNoTradeClose}
                session={modalNoTradeState.session}
                initialDateForAdd={modalNoTradeState.initialDateForAdd}
            />
            <ConfirmModal
                open={confirmModal.open}
                handleClose={handleConfirmClose}
                handleConfirm={handleConfirm}
                isLoading={confirmModal.isLoading}
            />
            <Modal
                open={currentModal === "profileIncomplete"}
                onClose={dismissModal}
                canClose={false}
                size="small"
            >
                <div className={styles.profileIncompleteModalContent}>
                    <Text variant="title" className={styles.profileIncompleteModalTitle}>
                        Mise à jour du profil trader
                    </Text>
                    <div className={styles.profileIncompleteModalWhy}>
                        <Text variant="body" className={styles.profileIncompleteModalMessage}>
                            Merci de compléter votre profil pour continuer à
                            enregistrer vos sessions.
                        </Text>
                    </div>
                    {missingUserFieldLabels.length > 0 && (
                        <Text
                            variant="body"
                            className={styles.profileIncompleteModalFocus}
                        >
                            <span className={styles.profileIncompleteModalFocusIcon}>👤</span>{" "}
                            {missingUserFieldLabels.length === 1
                                ? `${missingUserFieldLabels[0]} manquant`
                                : "Nom et prénom à renseigner"}
                        </Text>
                    )}
                    {questionsTotal - questionsAnswered > 0 && (
                        <Text
                            variant="body"
                            className={styles.profileIncompleteModalFocus}
                        >
                            <span className={styles.profileIncompleteModalFocusIcon}>🧩</span>{" "}
                            Il vous reste {questionsTotal - questionsAnswered} question
                            {questionsTotal - questionsAnswered > 1 ? "s" : ""} à compléter
                        </Text>
                    )}
                    {(questionsTotal - questionsAnswered > 0) && (
                        <Text
                            variant="caption"
                            className={styles.profileIncompleteModalDuration}
                        >
                            ⏱️ Environ 2 minutes
                        </Text>
                    )}
                    <div className={styles.profileIncompleteModalCtas}>
                        {missingUserFieldLabels.length > 0 && (
                            <Button
                                size="newSmall"
                                variant="primary"
                                label={
                                    missingUserFieldLabels.length === 1
                                        ? `Renseigner mon ${missingUserFieldLabels[0].toLowerCase()}`
                                        : "Renseigner nom et prénom"
                                }
                                onClick={() => {
                                    dismissModal();
                                    navigate("/account");
                                }}
                                className={styles.profileIncompleteModalCta}
                            />
                        )}
                        {questionsTotal - questionsAnswered > 0 && (
                            <Button
                                size="newSmall"
                                variant="primary"
                                label={`Compléter les questions (${questionsTotal - questionsAnswered} restante${questionsTotal - questionsAnswered > 1 ? "s" : ""})`}
                                onClick={() => {
                                    dismissModal();
                                    navigate("/account/questions");
                                }}
                                className={styles.profileIncompleteModalCta}
                            />
                        )}
                    </div>
                </div>
            </Modal>
            <Header icon={TradesIcon} title={title} {...headerProps} />
            {isOwnDashboard &&
                !isProfileCompletionLoading &&
                !isProfileComplete && (
                    <div className={styles.bannerRow}>
                        <ProfileIncompleteBanner
                            completionPath="/account/questions"
                            missingUserFieldLabels={missingUserFieldLabels}
                            questionsAnswered={questionsAnswered}
                            questionsTotal={questionsTotal}
                            completionPercent={completionPercent}
                        />
                    </div>
                )}
            {!isOwnDashboard &&
                (userDashboard?.enrollmentDate ||
                    userDashboard?.preferredTimeslot) && (
                    <div className={styles.userInfo}>
                        {userDashboard.enrollmentDate && (
                            <div className={styles.infoItem}>
                                <Text variant="body-bold">
                                    {isMobile
                                        ? "Depuis : "
                                        : "Membre depuis : "}
                                </Text>
                                <Text variant="body">
                                    {getMonthYearLabel(
                                        new Date(userDashboard.enrollmentDate),
                                    )}
                                </Text>
                            </div>
                        )}
                        {userDashboard.preferredTimeslot && (
                            <div className={styles.infoItem}>
                                <Text variant="body-bold">
                                    {isMobile
                                        ? "Créneau : "
                                        : "Créneau privilégié : "}
                                </Text>
                                <Text variant="body">
                                    {
                                        Timeslots[
                                            userDashboard.preferredTimeslot as keyof typeof Timeslots
                                        ]
                                    }
                                </Text>
                            </div>
                        )}
                    </div>
                )}
            {/* fin ajouts de Tony */}
            {/* ajouts de Tony - Section d'informations utilisateur */}
            <div className={styles.filters}>
                <div className={styles.datePickerContainer}>
                    <Text variant="body-bold">Créneau de dates</Text>
                    <DatePicker
                        mode="range"
                        placement="bottom-start"
                        startDate={startDate}
                        setStartDate={handleChangeStartDate}
                        endDate={endDate}
                        setEndDate={handleChangeEndDate}
                        classNameTrigger={styles.datePickerTrigger}
                    />
                </div>
                <div className={styles.selectContainer}>
                    <Text variant="body-bold">Créneaux</Text>
                    <Select
                        options={[
                            { label: "Matin", value: "morning" },
                            { label: "Après-midi", value: "afternoon" },
                            { label: "Soir", value: "evening" },
                        ]}
                        value={timeslotFilter}
                        onChange={(value) =>
                            handleTimeslotFilterChange(value as string)
                        }
                    />
                </div>
                <div className={styles.selectContainer}>
                    <Text variant="body-bold">Challenges</Text>
                    <Select
                        options={require("./filterOptions").CHALLENGE_OPTIONS}
                        value={challengeFilter}
                        onChange={(value) =>
                            handleChallengeFilterChange(value as string)
                        }
                    />
                </div>
                <div className={styles.selectContainer}>
                    <Text variant="body-bold">Style de trading</Text>
                    <Select
                        options={
                            require("./filterOptions").TRADING_STYLE_OPTIONS
                        }
                        value={tradingStyleFilter}
                        onChange={(value) =>
                            handleTradingStyleFilterChange(value as string)
                        }
                    />
                </div>
                {isOwnDashboard && (
                    <Button
                        size="medium"
                        variant="outlined"
                        onClick={togglePerformance}
                        label={
                            hidePerformance
                                ? isMobile
                                    ? "Afficher KPI"
                                    : "Afficher les perfs"
                                : isMobile
                                  ? "Masquer KPI"
                                  : "Masquer les perfs"
                        }
                        className={styles.perfKpisToggle}
                    />
                )}
                {hasFiltersBeenModified && (
                    <Button
                        size="medium"
                        variant="secondary"
                        onClick={handleResetFilters}
                        label={isMobile ? "Reset" : "Réinitialiser les filtres"}
                    />
                )}
            </div>
            <div className={styles.pageContent}>
                {!hidePerformance && (
                    <TradesTopSections
                        userId={userId!}
                        startDate={effectiveStartDate}
                        endDate={effectiveEndDate}
                        timeslotFilter={timeslotFilter}
                        challengeFilter={challengeFilter}
                        tradingStyleFilter={tradingStyleFilter}
                        timeslotCategories={timeslotCategories}
                        refreshTrigger={topSectionRefreshKey}
                    />
                )}
                <CalendarSessions
                    userId={userId!}
                    hidePerformance={hidePerformance}
                    className={styles.calendarSessions}
                    month={calendarMonth}
                    setMonth={setCalendarMonth}
                    effectiveStartDate={effectiveStartDate}
                    effectiveEndDate={effectiveEndDate}
                    timeslotFilter={timeslotFilter}
                    challengeFilter={challengeFilter}
                    tradingStyleFilter={tradingStyleFilter}
                    setTimeslotFilter={setTimeslotFilter}
                    timeslotCategories={timeslotCategories}
                    tradingSessionModalStateOpen={tradingSessionModalState.open}
                    noTradeModalStateOpen={modalNoTradeState.open}
                    modalDayDetailsState={modalDayDetailsState}
                    setModalDayDetailsState={setModalDayDetailsState}
                    handleDayDetailsOpen={handleDayDetailsOpen}
                    onEmptyWeekdayClick={
                        isOwnDashboard ? handleEmptyCalendarDayClick : undefined
                    }
                    isEmptyCalendarDayAddAllowed={
                        isOwnDashboard ? isCalendarEmptyDayAddAllowed : undefined
                    }
                    confirmModalOpen={confirmModal.open}
                />
            </div>
        </ConnectedLayout>
    );
};

export default Trades;

